import { useRef, useState } from "react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { Sparkle, PaperPlaneTilt, ArrowSquareOut, Image as ImageIcon, VideoCamera, LinkSimple, TextAlignLeft, TextAlignCenter, TextAlignRight, Eye, EyeSlash } from "@phosphor-icons/react";
import MarkdownBody from "@/components/MarkdownBody";

// ---------- Image downscaling ----------
// Client-side downscale-before-upload: images taken by phones easily hit
// 4–10 MB, which slows the mobile draft loop. Downscale to a max longest side
// of 1600 px at JPEG q=0.85 (typical result: 200–400 kB).
const MAX_SIDE = 1600;
const JPEG_Q = 0.85;

async function downscaleImage(file) {
  if (!file) return null;
  // Small files & non-JPEG/PNG/WEBP pass through untouched.
  if (file.size < 400 * 1024) {
    return await readAsDataURL(file);
  }
  const rawUrl = await readAsDataURL(file);
  try {
    const img = await new Promise((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = rej;
      i.src = rawUrl;
    });
    let { width, height } = img;
    if (Math.max(width, height) <= MAX_SIDE) return rawUrl;
    const scale = MAX_SIDE / Math.max(width, height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = width; canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, width, height);
    return canvas.toDataURL("image/jpeg", JPEG_Q);
  } catch {
    return rawUrl; // give up on downscale, keep the original
  }
}

function readAsDataURL(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

// One-shot flagship-article composer. Fills in title, subtitle, summary, body,
// category and tags -> POST /nature/stories/flagship publishes instantly and
// kicks off a Nano-Banana cover in the background. Any admin can now write an
// editorial in the browser instead of touching a Python script.

const CATEGORIES = [
  "climate", "weather", "biodiversity", "wildlife", "birds", "insects",
  "pollinators", "flowering-plants", "trees", "forests", "wildfires",
  "floods", "drought", "storms", "hurricanes", "tornadoes", "earthquakes",
  "volcanoes", "tsunamis", "landslides", "coasts", "oceans", "rivers",
  "lakes", "wetlands", "soil", "agriculture", "fisheries", "pollution",
  "air-quality", "water-quality", "conservation", "restoration",
  "citizen-science", "night-sky", "mountains", "night",
];

const LOCATION_SCOPES = [
  { value: "global", label: "🌍 Global" },
  { value: "continent", label: "🗺️ Continent" },
  { value: "country", label: "🏳️ Country" },
  { value: "region", label: "🏞️ Region / State" },
  { value: "city", label: "🏙️ City" },
];

export default function EditorialTab() {
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [summary, setSummary] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("climate");
  const [tags, setTags] = useState("");
  const [scope, setScope] = useState("global");
  const [country, setCountry] = useState("");
  const [continent, setContinent] = useState("");
  const [region, setRegion] = useState("");
  const [city, setCity] = useState("");
  const bodyRef = useRef(null);
  const imageFileRef = useRef(null);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(""); // "3 / 5"
  // Alignment for the *next* photo insertion — sticky between inserts.
  const [photoAlign, setPhotoAlign] = useState("center"); // "left" | "center" | "right"
  const [dragActive, setDragActive] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Insert `snippet` at the current caret position in the body textarea.
  // Falls back to appending at the end if the textarea isn't focused.
  const insertAtCaret = (snippet) => {
    const ta = bodyRef.current;
    if (!ta) { setBody((b) => (b ? `${b}\n${snippet}` : snippet)); return; }
    const start = ta.selectionStart ?? body.length;
    const end = ta.selectionEnd ?? body.length;
    const before = body.slice(0, start);
    const after = body.slice(end);
    // Make sure the block sits on its own line so the renderer picks it up.
    const prefix = before && !before.endsWith("\n") ? "\n\n" : "";
    const suffix = after && !after.startsWith("\n") ? "\n\n" : "";
    const next = `${before}${prefix}${snippet}${suffix}${after}`;
    setBody(next);
    // Restore caret right after the inserted snippet (async — after re-render).
    requestAnimationFrame(() => {
      const pos = (before + prefix + snippet).length;
      try { ta.focus(); ta.setSelectionRange(pos, pos); } catch { /* ignore */ }
    });
  };

  const onInsertPhotoClick = () => imageFileRef.current?.click();

  // Upload a list of files (from the picker or drag-drop) — downscales each,
  // POSTs to /uploads/photo, inserts the returned URLs at the caret. Two or
  // more consecutive image lines are rendered as a gallery grid by the
  // MarkdownBody component.
  const uploadFilesAndInsert = async (files) => {
    const list = Array.from(files || []).filter((f) => f && f.type?.startsWith("image/"));
    if (!list.length) return;
    // Cap to a sensible batch — dragging 30 photos should not stall the tab.
    const MAX = 12;
    const batch = list.slice(0, MAX);
    if (list.length > MAX) toast.message(`Uploading first ${MAX} of ${list.length} photos`);
    // 12 MB pre-downscale limit as a courtesy guard against truly huge files.
    for (const f of batch) {
      if (f.size > 32 * 1024 * 1024) {
        toast.error(`${f.name || "photo"} is too large (32 MB max)`);
        return;
      }
    }
    setUploadingImg(true);
    setUploadProgress(`0 / ${batch.length}`);
    const inserted = [];
    try {
      for (let i = 0; i < batch.length; i++) {
        const file = batch[i];
        setUploadProgress(`${i + 1} / ${batch.length}`);
        const dataUrl = await downscaleImage(file);
        const { data } = await http.post("/uploads/photo", { image_base64: dataUrl });
        const baseAlt = (file.name || "").replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ").slice(0, 80);
        // For a multi-file drop we render each on its own line — MarkdownBody
        // automatically collapses consecutive image lines into a gallery grid.
        // For alignment (single-photo focus), we only apply the L/R suffix
        // when the batch is a single file so the gallery grid stays uniform.
        const alt = batch.length === 1 && photoAlign !== "center"
          ? `${baseAlt}|${photoAlign}`
          : baseAlt;
        inserted.push(`![${alt}](${data.url})`);
      }
      // Insert all as consecutive lines (no blank line between — that's the
      // signal to render as a gallery when there are ≥2).
      insertAtCaret(inserted.join("\n"));
      if (batch.length === 1) {
        const where = photoAlign === "left" ? "left (text wraps)"
                    : photoAlign === "right" ? "right (text wraps)"
                    : "center";
        toast.success(`Photo inserted · ${where}`);
      } else {
        toast.success(`${batch.length} photos inserted as a gallery`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Photo upload failed");
    } finally {
      setUploadingImg(false);
      setUploadProgress("");
    }
  };

  const onPhotoFile = async (e) => {
    const files = e.target.files;
    e.target.value = "";
    await uploadFilesAndInsert(files);
  };

  // Drag-and-drop handlers on the textarea. We call preventDefault liberally
  // so the browser doesn't try to open the dropped image in a new tab.
  const onDragOver = (e) => {
    if (e.dataTransfer?.types?.includes("Files")) {
      e.preventDefault();
      setDragActive(true);
    }
  };
  const onDragLeave = () => setDragActive(false);
  const onDrop = async (e) => {
    setDragActive(false);
    if (!e.dataTransfer?.files?.length) return;
    e.preventDefault();
    await uploadFilesAndInsert(e.dataTransfer.files);
  };

  const onEmbedVideo = () => {
    const url = window.prompt("Paste a YouTube, Vimeo, or Loom link (any format):", "https://");
    if (!url) return;
    const trimmed = url.trim();
    if (!/^https?:\/\/\S+$/.test(trimmed)) return toast.error("That doesn't look like a valid URL");
    // Cheap sanity: is it a supported host?
    const supported = /(youtube\.com|youtu\.be|vimeo\.com|loom\.com)/i.test(trimmed);
    if (!supported) {
      const ok = window.confirm("This host might not embed automatically. Insert it anyway as a plain link?");
      if (!ok) return;
    }
    insertAtCaret(trimmed);
    toast.success("Video embedded — it will render in the published article");
  };

  const onInsertLink = () => {
    const url = window.prompt("URL:", "https://");
    if (!url) return;
    const trimmed = url.trim();
    if (!/^https?:\/\/\S+$/.test(trimmed)) return toast.error("Not a valid URL");
    const text = window.prompt("Link text (optional):", "") || trimmed;
    insertAtCaret(`[${text}](${trimmed})`);
  };
  const [generateCover, setGenerateCover] = useState(true);
  const [busy, setBusy] = useState(false);
  const [published, setPublished] = useState(null);

  const wordCount = body.trim() ? body.trim().split(/\s+/).length : 0;
  const readingMinutes = Math.max(1, Math.round(wordCount / 220));

  const buildLocation = () => {
    const loc = { scope };
    if (country.trim()) loc.country = country.trim();
    if (continent.trim()) loc.continent = continent.trim();
    if (region.trim()) loc.region = region.trim();
    if (city.trim()) loc.city = city.trim();
    return loc;
  };

  const publish = async () => {
    if (!title.trim() || !summary.trim() || !body.trim()) {
      toast.error("Title, summary and body are required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await http.post("/nature/stories/flagship", {
        title, subtitle, summary, body, category,
        tags: tags.split(/[,\n]/).map((t) => t.trim()).filter(Boolean),
        location: buildLocation(),
        generate_cover: generateCover,
      });
      setPublished(data);
      toast.success(
        generateCover
          ? "Published! Nano-Banana cover generating in the background…"
          : "Published!",
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setTitle(""); setSubtitle(""); setSummary(""); setBody("");
    setTags(""); setCountry(""); setContinent(""); setRegion(""); setCity("");
    setPublished(null);
  };

  return (
    <div className="space-y-6" data-testid="editorial-tab">
      <div className="rounded-2xl border border-black/15 bg-white p-5">
        <div className="flex items-center gap-2 text-emerald-700">
          <Sparkle size={18} weight="fill" />
          <span className="text-sm font-medium">Write a flagship editorial</span>
        </div>
        <p className="mt-1 text-xs text-[color:var(--np-muted,#3A3830)]">
          Paste any long-form article. It publishes instantly as an approved flagship
          under <code className="text-emerald-700">/articles</code> and its category
          channel on <code className="text-emerald-700">/observations</code>. A
          photorealistic cover is generated in the background.
        </p>
      </div>

      {published ? (
        <div className="rounded-2xl border border-emerald-600/30 bg-emerald-50 p-5">
          <div className="text-emerald-700 text-sm font-medium">
            Published: {published.story?.title}
          </div>
          <div className="text-xs text-[color:var(--np-muted,#3A3830)] mt-1">
            id: <code className="text-emerald-800">{published.id}</code>
            {published.cover_pending && " · cover generating…"}
          </div>
          <div className="mt-3 flex gap-3">
            <a
              href={`/article/${published.id}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-800"
              data-testid="editorial-view-link"
            >
              <ArrowSquareOut size={14} /> View article
            </a>
            <button
              onClick={reset}
              className="text-xs text-[color:var(--np-muted,#3A3830)] hover:text-[#151312]"
              data-testid="editorial-write-another"
            >
              Write another
            </button>
          </div>
        </div>
      ) : null}

      <div className="grid gap-4">
        <div>
          <label className="block text-xs text-[color:var(--np-muted,#3A3830)] mb-1">Title</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="India on the Climate Front Line"
            data-testid="editorial-title"
            className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm text-[#151312] placeholder:text-[color:var(--np-muted,#3A3830)]/70 focus:outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
          />
        </div>

        <div>
          <label className="block text-xs text-[color:var(--np-muted,#3A3830)] mb-1">Subtitle</label>
          <input
            value={subtitle}
            onChange={(e) => setSubtitle(e.target.value)}
            placeholder="Optional deck — appears under the headline"
            data-testid="editorial-subtitle"
            className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm text-[#151312] placeholder:text-[color:var(--np-muted,#3A3830)]/70 focus:outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
          />
        </div>

        <div>
          <label className="block text-xs text-[color:var(--np-muted,#3A3830)] mb-1">
            Summary <span className="text-[color:var(--np-muted,#3A3830)]/70">(shown on cards, 1–3 sentences)</span>
          </label>
          <textarea
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            rows={3}
            data-testid="editorial-summary"
            className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm text-[#151312] placeholder:text-[color:var(--np-muted,#3A3830)]/70 focus:outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
          />
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <label className="block text-xs text-[color:var(--np-muted,#3A3830)] mb-1">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="editorial-category"
              className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm text-[#151312] placeholder:text-[color:var(--np-muted,#3A3830)]/70 focus:outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-[color:var(--np-muted,#3A3830)] mb-1">Location scope</label>
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              data-testid="editorial-scope"
              className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm text-[#151312] placeholder:text-[color:var(--np-muted,#3A3830)]/70 focus:outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
            >
              {LOCATION_SCOPES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-[color:var(--np-muted,#3A3830)] mb-1">Tags (comma-separated)</label>
            <input
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="monsoon, drought, farmers"
              data-testid="editorial-tags"
              className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm text-[#151312] placeholder:text-[color:var(--np-muted,#3A3830)]/70 focus:outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
            />
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <input
            value={continent}
            onChange={(e) => setContinent(e.target.value)}
            placeholder="Continent (optional)"
            data-testid="editorial-continent"
            className="rounded-lg border border-black/15 bg-white px-3 py-2 text-sm text-[#151312] placeholder:text-[color:var(--np-muted,#3A3830)]/70 focus:outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
          />
          <input
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            placeholder="Country (optional)"
            data-testid="editorial-country"
            className="rounded-lg border border-black/15 bg-white px-3 py-2 text-sm text-[#151312] placeholder:text-[color:var(--np-muted,#3A3830)]/70 focus:outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
          />
          <input
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            placeholder="Region / state (optional)"
            data-testid="editorial-region"
            className="rounded-lg border border-black/15 bg-white px-3 py-2 text-sm text-[#151312] placeholder:text-[color:var(--np-muted,#3A3830)]/70 focus:outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
          />
          <input
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="City (optional)"
            data-testid="editorial-city"
            className="rounded-lg border border-black/15 bg-white px-3 py-2 text-sm text-[#151312] placeholder:text-[color:var(--np-muted,#3A3830)]/70 focus:outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs text-[color:var(--np-muted,#3A3830)]">
              Body <span className="text-[color:var(--np-muted,#3A3830)]/70">(plain text or Markdown — use ## for section headings)</span>
            </label>
            <span className="text-[11px] text-[color:var(--np-muted,#3A3830)]">
              {wordCount.toLocaleString()} words · ~{readingMinutes} min read
            </span>
          </div>
          {/* Insert toolbar — photos, videos, links inline at the caret */}
          <div className="mb-2 flex flex-wrap items-center gap-2" data-testid="editorial-body-toolbar">
            <button
              type="button"
              onClick={onInsertPhotoClick}
              disabled={uploadingImg}
              data-testid="editorial-insert-photo"
              className="inline-flex items-center gap-1.5 rounded-full border border-black/15 bg-white hover:bg-black/5 px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#151312] disabled:opacity-50"
            >
              <ImageIcon size={12} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
              {uploadingImg ? `Uploading ${uploadProgress}` : "Insert photos"}
            </button>

            {/* Alignment segmented control — sticks between inserts */}
            <div
              role="radiogroup"
              aria-label="Photo alignment"
              className="inline-flex rounded-full border border-black/15 bg-white p-0.5"
              data-testid="editorial-photo-align"
            >
              {[
                { key: "left", Icon: TextAlignLeft, label: "Left" },
                { key: "center", Icon: TextAlignCenter, label: "Center" },
                { key: "right", Icon: TextAlignRight, label: "Right" },
              ].map(({ key, Icon, label }) => {
                const on = photoAlign === key;
                return (
                  <button
                    key={key}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    onClick={() => setPhotoAlign(key)}
                    data-testid={`editorial-photo-align-${key}`}
                    title={`Align next photo · ${label}${key === "left" || key === "right" ? " (text wraps)" : ""}`}
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-1 font-mono-tech text-[9px] tracking-[0.24em] uppercase transition-colors ${
                      on ? "bg-[#151312] text-[#F5EBDC]" : "text-[#151312] hover:bg-black/5"
                    }`}
                  >
                    <Icon size={12} weight="bold" />
                    {label}
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              onClick={onEmbedVideo}
              data-testid="editorial-embed-video"
              className="inline-flex items-center gap-1.5 rounded-full border border-black/15 bg-white hover:bg-black/5 px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#151312]"
            >
              <VideoCamera size={12} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
              Embed video
            </button>
            <button
              type="button"
              onClick={onInsertLink}
              data-testid="editorial-insert-link"
              className="inline-flex items-center gap-1.5 rounded-full border border-black/15 bg-white hover:bg-black/5 px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#151312]"
            >
              <LinkSimple size={12} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
              Link
            </button>

            {/* Preview toggle */}
            <button
              type="button"
              onClick={() => setPreviewOpen((v) => !v)}
              data-testid="editorial-preview-toggle"
              aria-pressed={previewOpen}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.24em] uppercase transition-colors ${
                previewOpen
                  ? "border-transparent bg-[#151312] text-[#F5EBDC]"
                  : "border-black/15 bg-white text-[#151312] hover:bg-black/5"
              }`}
            >
              {previewOpen ? <EyeSlash size={12} weight="fill" /> : <Eye size={12} weight="fill" />}
              {previewOpen ? "Hide preview" : "Live preview"}
            </button>

            <span className="ml-auto text-[10px] text-[color:var(--np-muted,#3A3830)]/85">
              Drag photos onto the field · Multi-select for a gallery · YT / Vimeo / Loom auto-embed
            </span>
          </div>
          <input
            ref={imageFileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={onPhotoFile}
            className="hidden"
            data-testid="editorial-photo-input"
          />

          {/* Editor + optional side-by-side live preview */}
          <div className={`grid gap-3 ${previewOpen ? "md:grid-cols-2" : "grid-cols-1"}`}>
            <div
              className={`relative rounded-lg transition-colors ${dragActive ? "ring-2 ring-[color:var(--np-brand,#B85F2A)] ring-offset-2 ring-offset-[color:var(--np-bg,#F5EBDC)]" : ""}`}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              data-testid="editorial-body-dropzone"
            >
              <textarea
                ref={bodyRef}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={22}
                placeholder={`Paste your article here.\n\n## First section heading\n\nOpening paragraph…\n\nDrag photos anywhere onto this field, or use the buttons above.`}
                data-testid="editorial-body"
                className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm text-[#151312] leading-relaxed placeholder:text-[color:var(--np-muted,#3A3830)]/70 focus:outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
              />
              {dragActive && (
                <div className="pointer-events-none absolute inset-0 grid place-items-center rounded-lg bg-white/85 backdrop-blur-sm">
                  <div className="font-mono-tech text-[11px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] inline-flex items-center gap-2">
                    <ImageIcon size={14} weight="fill" /> Drop to upload · gallery if multiple
                  </div>
                </div>
              )}
            </div>
            {previewOpen && (
              <div
                data-testid="editorial-preview"
                className="rounded-lg border border-black/15 bg-white px-4 py-4 max-h-[560px] overflow-y-auto"
              >
                <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)] mb-2">
                  Live preview
                </div>
                {body.trim() ? (
                  <MarkdownBody text={body} />
                ) : (
                  <p className="text-sm text-[color:var(--np-muted,#3A3830)] italic">
                    Start typing to see the article render here — photos, galleries, videos and links all show live.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-[color:var(--np-muted,#3A3830)]">
          <input
            type="checkbox"
            checked={generateCover}
            onChange={(e) => setGenerateCover(e.target.checked)}
            data-testid="editorial-generate-cover"
            className="accent-[#151312]"
          />
          Generate a Nano-Banana cover image after publishing
        </label>

        <div className="flex items-center gap-3">
          <button
            onClick={publish}
            disabled={busy || !title.trim() || !summary.trim() || !body.trim()}
            data-testid="editorial-publish-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-[#151312] px-4 py-2 text-sm font-medium text-[#F5EBDC] disabled:opacity-50"
          >
            <PaperPlaneTilt size={16} weight="fill" />
            {busy ? "Publishing…" : "Publish flagship article"}
          </button>
          <span className="text-xs text-[color:var(--np-muted,#3A3830)]">
            Auto-approved · goes live on /articles instantly
          </span>
        </div>
      </div>
    </div>
  );
}
